import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Pressable, ScrollView, View } from "react-native";
import { useDispatch } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import InputChoose from "@/components/input/InputChoose";
import InputChooseList from "@/components/input/InputChooseList";
import InputSelection from "@/components/input/InputSelection";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { sendRequest } from "@/utils/ApiUtil";
import { CorporateKycObject } from "@/utils/dto/application.request";
import {
  ProfileCorporateKYCRegulatedStatus,
  ProfileCorporateKYCEngageInActivities,
  ProfileCorporateKYCPrimaryBusiness,
  ProfileKYCVendorsAndCounterparties,
  BooleanEnum,
  PrimaryPurposeEnum,
} from "@/utils/dto/extracted_enums";

type FormData = {
  regulatedStatus: string;
  businessJurisdictions: string[];
  sendJurisdictions: string[];
  receiveJurisdictions: string[];
  engageInActivities: string[];
  vendorsAndCounterparties: string[];
  primaryBusiness: string;
  primaryPurpose: string;
  isDao: string;
  transmitsCustomerFunds: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

export default function Kyc() {
  const [inputArray, setInputArray] = useState<{
    [key: string]: { value: string; code: string }[];
  }>({});
  const [inputData, setInputData] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [businessJurisdictionsData, setBusinessJurisdictionsData] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [businessSearchTerm, setBusinessSearchTerm] = useState("");
  const [isCorporationApplication, setIsCorporationApplication] =
    useState(false);

  const states = useAppSelector((state: any) => state);

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const resolver = classValidatorResolver(CorporateKycObject, {
    validator: { skipMissingProperties: true },
  });

  const { handleSubmit, formState, control, setValue } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const { isSubmitting } = formState;

  useEffect(() => {
    const applicationType = storage.getString("user.auth.applicationType");
    if (applicationType === "CORPORATION") {
      setIsCorporationApplication(true);
    } else {
      setValue("regulatedStatus", "OUT", { shouldValidate: false });
      setValue("businessJurisdictions", ["OUT"], {
        shouldValidate: false,
      });
      setValue("vendorsAndCounterparties", ["OUT"], {
        shouldValidate: false,
      });
      setValue("isDao", "OUT", { shouldValidate: false });
      setValue("transmitsCustomerFunds", "OUT", { shouldValidate: false });
      setValue("primaryBusiness", "OUT", { shouldValidate: false });
    }
  }, []);

  useEffect(() => {
    const businessJurisdictionsCodes = inputArray.businessJurisdictions?.map(
      (item) => item.code
    );
    if (businessJurisdictionsCodes && businessJurisdictionsCodes.length > 0) {
      setValue("businessJurisdictions", businessJurisdictionsCodes);
    }
    const sendJurisdictionsCodes = inputArray.sendJurisdictions?.map(
      (item) => item.code
    );

    if (sendJurisdictionsCodes && sendJurisdictionsCodes.length > 0) {
      setValue("sendJurisdictions", sendJurisdictionsCodes);
    }

    const receiveJurisdictionsCodes = inputArray.receiveJurisdictions?.map(
      (item) => item.code
    );

    if (receiveJurisdictionsCodes && receiveJurisdictionsCodes.length > 0) {
      setValue("receiveJurisdictions", receiveJurisdictionsCodes);
    }

    const engageInActivitiesCodes = inputArray.engageInActivities?.map(
      (item) => item.code
    );
    if (engageInActivitiesCodes && engageInActivitiesCodes.length > 0) {
      setValue("engageInActivities", engageInActivitiesCodes);
    }

    const vendorsAndCounterpartiesCodes =
      inputArray.vendorsAndCounterparties?.map((item) => item.code);
    if (
      vendorsAndCounterpartiesCodes &&
      vendorsAndCounterpartiesCodes.length > 0
    ) {
      setValue("vendorsAndCounterparties", vendorsAndCounterpartiesCodes);
    }
  }, [inputArray]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setInputData(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          searchTerm: businessSearchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setBusinessJurisdictionsData(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (businessSearchTerm) {
      fetchData();
    }
  }, [businessSearchTerm]);

  const handleSelection = (
    data: { value: string; code: string },
    name: string
  ) => {
    setInputArray((prevState) => {
      const isValueAlreadyPresent = prevState[name]?.some(
        (item) => item.value === data.value
      );

      if (isValueAlreadyPresent) {
        return prevState;
      }

      return {
        ...prevState,
        [name]: [
          ...(prevState[name] || []),
          { value: data.value, code: data.code },
        ],
      };
    });
    setInputData([]);
    setBusinessJurisdictionsData([]);
  };

  const handleDeleteItem = (key: string, value: string, code: string) => {
    setInputArray((prevState) => ({
      ...prevState,
      [key]: prevState[key]?.filter((item) => item.value !== value),
    }));
  };

  const onSubmit = async (data: FormData) => {
    const filteredData: { [key: string]: string | string[] | boolean } = {};
    let fundsSendReceiveJurisdictions: string[] = [];

    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        if (!(value.length === 1 && value[0] === "OUT")) {
          if (key === "sendJurisdictions" || key === "receiveJurisdictions") {
            fundsSendReceiveJurisdictions =
              fundsSendReceiveJurisdictions.concat(value);
          } else {
            filteredData[key] = value.filter((item) => item !== "OUT");
          }
        }
      } else if (value === "NO") {
        filteredData[key] = false;
      } else if (value === "YES") {
        filteredData[key] = true;
      } else if (value !== "OUT") {
        filteredData[key] = value;
      }
    }

    const removeDuplicates = fundsSendReceiveJurisdictions.filter(
      (index, i) => fundsSendReceiveJurisdictions.indexOf(index) === i
    );

    filteredData["fundsSendReceiveJurisdictions"] = removeDuplicates;
    dispatch(setForm({ kycData: { ...states.kycData, ...filteredData } }));
    router.push("/investmentprofile");
  };
  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <Logo className="m-4" />
      <View className="m-1">
        <Title text={t("onboarding/kyc:title")} />
      </View>
      <View className="m-1">
        <Subtitle text={t("onboarding/kyc:subtitle")} />
      </View>
      <View className="z-50 items-center h-fit w-full p-6 lg:border lg:border-solid lg:border-gray lg:shadow lg:rounded-md lg:w-3/5">
        <Pressable
          onPress={() => router.back()}
          className="absolute z-[110] top-0 left-4 lg:left-6 lg:top-10"
        >
          <AntDesign name="arrowleft" size={24} color="#BDBDBD" />
        </Pressable>
        <View className="z-[100] max-w-80">
          <InputSelection
            control={control}
            name="receiveJurisdictions"
            label={t("onboarding/kyc:input.receivejurisdictions")}
            data={inputData || []}
            placeholder={t("common:placeholder.typetosearch")}
            onSearchChange={(query) => setSearchTerm(query)}
            clearValueFunction={() => setInputData([])}
            handleChange={(data: { value: string; code: string }) => {
              handleSelection(data, "receiveJurisdictions");
            }}
            selectionArray={inputArray.receiveJurisdictions || []}
            deleteItem={(value, code) =>
              handleDeleteItem("receiveJurisdictions", value, code)
            }
          />
        </View>
        <View className="z-[90] max-w-80">
          <InputSelection
            control={control}
            name="sendJurisdictions"
            label={t("onboarding/kyc:input.sendjurisdictions")}
            data={inputData || []}
            placeholder={t("common:placeholder.typetosearch")}
            onSearchChange={(query) => setSearchTerm(query)}
            clearValueFunction={() => setInputData([])}
            handleChange={(data: { value: string; code: string }) => {
              handleSelection(data, "sendJurisdictions");
            }}
            selectionArray={inputArray.sendJurisdictions || []}
            deleteItem={(value, code) =>
              handleDeleteItem("sendJurisdictions", value, code)
            }
          />
        </View>
        <View className="z-[80] max-w-80">
          <InputChoose
            control={control}
            name="engageInActivities"
            label={t("onboarding/kyc:input.engageinactivities")}
            data={{ ProfileCorporateKYCEngageInActivities }}
            isMultipleSelection
            selectionArray={inputArray.engageInActivities || []}
            handleSelectedItem={(data: { value: string; code: string }) => {
              handleSelection(data, "engageInActivities");
            }}
            deleteItem={(value, code) =>
              handleDeleteItem("engageInActivities", value, code)
            }
          />
        </View>
        <View className="z-[70] max-w-80">
          <InputChooseList
            control={control}
            name="primaryPurpose"
            label={t("onboarding/kyc:input.primarypurpose")}
            data={{ PrimaryPurposeEnum }}
          />
        </View>
        {isCorporationApplication && (
          <>
            <View className="z-[60] max-w-80">
              <InputChoose
                control={control}
                name="vendorsAndCounterparties"
                label={t("onboarding/kyc:input.vendorsandcounterparties")}
                data={{ ProfileKYCVendorsAndCounterparties }}
                isMultipleSelection
                selectionArray={inputArray.vendorsAndCounterparties || []}
                handleSelectedItem={(data: { value: string; code: string }) => {
                  handleSelection(data, "vendorsAndCounterparties");
                }}
                deleteItem={(value, code) =>
                  handleDeleteItem("vendorsAndCounterparties", value, code)
                }
              />
            </View>
            <View className="z-50 max-w-80">
              <InputChooseList
                control={control}
                name="primaryBusiness"
                label={t("onboarding/kyc:input.primarybusiness")}
                data={{ ProfileCorporateKYCPrimaryBusiness }}
              />
            </View>
            <View className="z-40 max-w-80">
              <InputSelection
                control={control}
                name="businessJurisdictions"
                label={t("onboarding/kyc:input.businessjurisdictions")}
                data={businessJurisdictionsData || []}
                placeholder={t("common:placeholder.typetosearch")}
                onSearchChange={(query) => setBusinessSearchTerm(query)}
                clearValueFunction={() => setBusinessJurisdictionsData([])}
                handleChange={(data: { value: string; code: string }) => {
                  handleSelection(data, "businessJurisdictions");
                }}
                selectionArray={inputArray.businessJurisdictions || []}
                deleteItem={(value, code) =>
                  handleDeleteItem("businessJurisdictions", value, code)
                }
              />
            </View>
            <View className="z-30 max-w-80">
              <InputChoose
                control={control}
                name="regulatedStatus"
                label={t("onboarding/kyc:input.regulatedstatus")}
                data={{ ProfileCorporateKYCRegulatedStatus }}
              />
            </View>
            <View className="z-20 max-w-80">
              <InputChoose
                control={control}
                name="isDao"
                label={t("onboarding/kyc:input.isdao")}
                data={{ BooleanEnum }}
              />
            </View>
            <View className="z-10 max-w-80">
              <InputChoose
                control={control}
                name="transmitsCustomerFunds"
                label={t("onboarding/kyc:input.transmitscustomerfunds")}
                data={{ BooleanEnum }}
              />
            </View>
          </>
        )}
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
      <View className="flex-grow justify-end">
        <Copyright />
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
